import { useEffect, useMemo, useRef, useState } from 'react';
import type { UserSummary } from '@todo/shared';
import { signApi } from '../api/sign.api';

/** What a pick hands back: enough to fill name, email and designation. */
export interface PickedPerson {
  displayName: string;
  email: string | null;
  jobTitle: string | null;
  /** Set for someone found in ANOTHER organisation's directory. */
  orgName?: string | null;
}

/**
 * A text box that suggests organisation members as you type.
 *
 * STILL A TEXT BOX FIRST: an outside signer has no account here, so free
 * typing must keep working - suggestions are an offer, never a gate. Picking
 * one hands the whole person back (`onPick`) so the caller can fill email and
 * designation as well as the name.
 *
 * WHERE THE SUGGESTIONS COME FROM: the `people` the page already loaded
 * (instant, no request per keystroke). That list is capped at 100 by the page,
 * so when it is full we ALSO ask the server's search - debounced - rather than
 * silently failing to find the 101st person.
 */

function matches(p: UserSummary, q: string): boolean {
  return (
    p.displayName.toLowerCase().includes(q)
    || (p.email ?? '').toLowerCase().includes(q)
    || p.username.toLowerCase().includes(q)
    || (p.employeeId ?? '').toLowerCase().includes(q)
  );
}

export function NameAutocomplete({
  value,
  onChange,
  onPick,
  people,
  excludeEmails = [],
  placeholder,
  className,
}: {
  value: string;
  onChange: (text: string) => void;
  onPick: (person: PickedPerson) => void;
  /** Org members the page already has (people WITHOUT an email can't sign
   *  and are filtered out here as well, whatever the caller passes). */
  people: UserSummary[];
  /** Emails already on the document - suggesting them again is noise. */
  excludeEmails?: string[];
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [remote, setRemote] = useState<PickedPerson[]>([]);
  const wrapRef = useRef<HTMLDivElement>(null);

  const query = value.trim().toLowerCase();

  // Cross-organisation search: sign documents can go to people in ANY
  // organisation, so the server is asked (debounced) once there are two
  // characters - the local list only covers the caller's own organisation.
  useEffect(() => {
    if (!open || query.length < 2) {
      setRemote([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void signApi
        .people(value.trim(), controller.signal)
        .then((r) => setRemote(r.map((p) => ({
          displayName: p.name, email: p.email, jobTitle: p.designation, orgName: p.orgName,
        }))))
        .catch(() => setRemote([]));
    }, 250);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [open, query, value]);

  const suggestions = useMemo(() => {
    if (query.length === 0) return [];
    const taken = new Set(excludeEmails.map((e) => e.toLowerCase()));
    const seen = new Set<string>();
    const out: PickedPerson[] = [];
    const own: PickedPerson[] = people
      .filter((x) => matches(x, query))
      .map((x) => ({ displayName: x.displayName, email: x.email, jobTitle: x.jobTitle }));
    for (const p of [...own, ...remote]) {
      const email = p.email?.toLowerCase();
      if (!email || seen.has(email) || taken.has(email)) continue;
      seen.add(email);
      out.push(p);
      if (out.length === 8) break;
    }
    return out;
  }, [people, remote, query, excludeEmails]);

  useEffect(() => { setActive(0); }, [query]);

  function choose(person: PickedPerson) {
    onPick(person);
    setOpen(false);
  }

  const showList = open && suggestions.length > 0;

  return (
    <div
      ref={wrapRef}
      className={`relative ${className ?? ''}`}
      onBlur={(e) => {
        // Close only when focus leaves the whole widget, not on the way to
        // a suggestion.
        if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <input
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={showList}
        aria-autocomplete="list"
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!showList) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => (a + 1) % suggestions.length);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => (a - 1 + suggestions.length) % suggestions.length);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const picked = suggestions[active];
            if (picked) choose(picked);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        className="w-full min-w-0 rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-sm outline-none transition focus:border-brand-500"
      />
      {showList && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 z-30 mt-1 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          {suggestions.map((p, i) => (
            <li
              key={p.email ?? p.displayName}
              role="option"
              aria-selected={i === active}
              // mousedown, not click: the input's blur would otherwise close
              // the list before the click lands.
              onMouseDown={(e) => { e.preventDefault(); choose(p); }}
              onMouseEnter={() => setActive(i)}
              className={`cursor-pointer px-2.5 py-1.5 ${i === active ? 'bg-slate-100' : ''}`}
            >
              <span className="block truncate text-sm text-slate-800">{p.displayName}</span>
              <span className="block truncate text-xs text-slate-500">
                {p.email}{p.jobTitle ? ` · ${p.jobTitle}` : ''}{p.orgName ? ` · ${p.orgName}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
